import { observer } from 'mobx-react-lite';
import React, { FunctionComponent } from 'react';

import store from '@/store';
import agentChatStore from '@/store/agentChatStore';
import kbStore from '@/store/aiKnowledgeBase';
import autoTourStore from '@/store/autoTour';
import businessStore from '@/store/business';
import logAnalyzeStore from '@/store/logAnalyze';

type StoreProps = {
  /** 全局store */
  store: typeof store;
  /** 新手指引store */
  autoTourStore: typeof autoTourStore;
  /** 业务信息store */
  businessStore: typeof businessStore;
  /** 知识库store */
  kbStore: typeof kbStore;
  /** 日志分析store */
  logAnalyzeStore: typeof logAnalyzeStore;
  /** ai助手store */
  agentChatStore: typeof agentChatStore;
};

/**
 * 高阶组件，将React组件包装成响应式组件并注入全局store
 *
 * @description
 * 该高阶组件有两个主要功能:
 * 1. 使用mobx的observer将组件变成响应式的
 * 2. 自动注入全局store，使组件可以访问共享状态
 *
 * @example
 * ```tsx
 * import React from 'react';
 * interface Props {
 *  title: string;
 * }
 * const MyComponent = ObserverStore<Props>(({ title, store }) => {
 *  return (
 *    <div>
 *      <p>User: {store.appId}</p>
 *    </div>
 *  );
 * });
 * export default MyComponent;
 * ```
 *
 * @template TProps - 组件的props类型
 * @param {ComponentType<TProps & StoreProps>} Component - 需要被包装的React组件
 * @returns {FunctionComponent<TProps>} 包装后的响应式组件
 */
export function ObserverStore<TProps extends object>(
  Component: FunctionComponent<TProps & StoreProps>
): FunctionComponent<TProps> {
  // 使用observer包装组件使其响应式
  const ObservedComponent = observer(Component);

  // 包装组件并注入store
  const WrappedComponent = (props: TProps) => (
    <ObservedComponent
      {...props}
      store={store}
      autoTourStore={autoTourStore}
      businessStore={businessStore}
      kbStore={kbStore}
      logAnalyzeStore={logAnalyzeStore}
      agentChatStore={agentChatStore}
    />
  );

  return WrappedComponent;
}

export type { StoreProps };
